import { useEffect, useMemo, useState } from "react";
import FiltroPeriodo from "../components/FiltroPeriodo";
import FiltroTipo from "../components/FiltroTipo";
import BicoCard from "../components/BicoCard";
import { getJSON } from "../services/api";
import { analyzeResumo, loadConfigAnalise } from "../analytics/resumoInsights";
import DmaSelector from "../components/DmaSelector";
import KpiCard from "../components/KpiCard";
import { ChartCard } from "../components/ChartCard";
import ActionCard from "../components/ActionCard";
import StatPills from "../components/StatPills";
import { GroupGuard } from "../auth/GroupGuard";

const REQUIRED_GROUPS = [
  "bcba776b-ad66-4c3c-b253-a10a1b65c294", // Admins - TI
  "ccf4a2fb-4ccb-4292-ba83-6c565146e4f0", // Users - TI
  "1c4b0acb-3657-48c1-9024-0904307a94b7", // CEP - Admins
  "c7b622a4-bdb6-48b4-b39e-9f0970a9602f", // CEP - Users
];

type Periodo = {
  inicio?: string;
  fim?: string;
  start?: string;
  end?: string;
};

type ProdutoAgg = {
  cod: string;
  desc: string;
  leituras: number;
  share: number;
};

type LoteAgg = {
  lote: string;
  leituras: number;
  share: number;
};

type RiskTop = {
  bico: number;
  score: number;
  media: number;
  violacao: number;
  drift: number;
  dma?: number;
  dmaCentro?: number;
  dmaLimite?: number;
  dmaMisto?: number;
  operadores?: string[];
};

type Insight = {
  summary: string[];
  risksTop: RiskTop[];
  operatorLoad: any[];
  operatorIssues: any[];
  operatorUnderUtilization?: any[];
  kpis?: any;
  charts?: any;
  dma?: any;
  meta?: any;
};

export type BicoResumo = {
  bico: number;
  status: "fora" | "tolerancia" | "ideal";
  produtos: {
    codigos: string[];
    descricoes: string[];
    principal: string | null;
    quantidade: number;
  };
  lotes: {
    lista: string[];
    quantidade: number;
  };
  pesagens: number;
  valor: number;
  menor: number;
  maior: number;
  idealMin: number;
  idealMax: number;
  pctAbaixo: number;
  pctAcima: number;
  pctTolerancia: number;
  pctIdeal: number;
  operador: string;
  colaboradoresArr: string[];
  matriculasArr: string[];
  totaisReaisProduto?: Record<string, number>;
};

const SCORE_MIN = 50;
const DMA_MIN = 0.4;

type DataPoint = { label: string; [key: string]: string | number | null };

function datasetFromLabelsSeries(
  labels: (string | number)[] = [],
  series: { key: string; values?: (number | null)[] }[] = []
): DataPoint[] {
  const len = labels.length;
  const base: DataPoint[] = Array.from({ length: len }, (_, i) => ({
    label: String(labels[i]),
  }));
  for (const s of series) {
    for (let i = 0; i < len; i++) base[i][s.key] = s.values?.[i] ?? null;
  }
  return base;
}

function formatColaboradores(nomes?: string): string {
  if (!nomes) return "";

  return nomes
    .split(",")
    .map((nome) => {
      let partes = nome.trim().split(/\s+/);

      // Se começar com número e traço, mantém isso separado
      let prefixo = "";
      if (/^\d+$/.test(partes[0])) {
        prefixo = partes[0]; // número
        partes.shift();
        if (partes[0] === "-") {
          prefixo += " -";
          partes.shift();
        }
      }

      if (partes.length === 0) return prefixo;

      const primeiro = partes[0];
      const ultimo = partes.length > 1 ? partes[partes.length - 1] : "";

      return (
        (prefixo ? prefixo + " " : "") +
        primeiro.charAt(0).toUpperCase() +
        primeiro.slice(1).toLowerCase() +
        (ultimo
          ? " " + ultimo.charAt(0).toUpperCase() + ultimo.slice(1).toLowerCase()
          : "")
      );
    })
    .join(", ");
}

function makeDmaHint(kpis: any, dma: any): string {
  if (!kpis) return "";
  const ref = Number.isFinite(Number(kpis.dmaRefValue))
    ? Number(kpis.dmaRefValue).toFixed(1)
    : "-";
  const mode = kpis.dmaMode;
  if (mode === "center") return `Ref.: centro ideal ${ref} g`;
  if (mode === "limit") return `Ref.: limite ideal ${ref} g`;
  const inside = dma?.usado?.pesos?.inside ?? 0.4;
  const outside = dma?.usado?.pesos?.outside ?? 1;
  return `Pondera ${inside}× dentro / ${outside}× fora do ideal. Ref.: centro ${ref} g`;
}

function BarList<T>({
  items = [],
  getKey,
  getLabel,
  getValue,
  onClick,
  maxBars = 8,
}: {
  items: T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getValue: (item: T) => number;
  onClick?: (item: T) => void;
  maxBars?: number;
}) {
  const top = items.slice(0, maxBars);
  const max = top.reduce((m, x) => Math.max(m, getValue(x)), 0) || 1;
  return (
    <ul className="mt-2 space-y-2">
      {top.map((it) => {
        const val = getValue(it);
        const w = Math.max(6, Math.round((val / max) * 100));
        return (
          <li
            key={getKey(it)}
            className="cursor-pointer"
            onClick={() => onClick?.(it)}
          >
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-medium truncate">{getLabel(it)}</span>
              <span className="text-slate-700 ml-2">{val?.toFixed(0)} leit.</span>
            </div>
            <div className="h-2 rounded bg-slate-200">
              <div
                className="h-2 rounded"
                style={{ width: `${w}%`, backgroundColor: "#2563eb" }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function ResumoEnvasadora() {
  const [cfg, setCfg] = useState<any>(null);
  const [periodo, setPeriodo] = useState<Periodo | null>(null);
  const [filtro, setFiltro] = useState<"todos" | "fora" | "tolerancia" | "ideal">("todos");
  const [data, setData] = useState<BicoResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<Insight>({
    summary: [],
    risksTop: [],
    operatorLoad: [],
    operatorIssues: [],
  });
  const [operadoresUI, setOperadoresUI] = useState<string[]>([]);
  const [prodSel, setProdSel] = useState<string | null>(null);
  const [loteSel, setLoteSel] = useState<string | null>(null);

  const [dmaMode, setDmaMode] = useState<"mixed" | "center" | "limit">("mixed");
  const [dmaInside, setDmaInside] = useState<number>(0.4);
  const [dmaOutside, setDmaOutside] = useState<number>(1);

  function ymdLocal(d: string | Date) {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    const h = String(dt.getHours()).padStart(2, "0");
    const min = String(dt.getMinutes()).padStart(2, "0");
    const s = String(dt.getSeconds()).padStart(2, "0");
    return `${y}-${m}-${day} ${h}:${min}:${s}`;
  }


  function rangePadraoHoje() {
    const now = new Date();
    const ymd = ymdLocal(now);
    return { start: ymd, end: ymd };
  }

  useEffect(() => {
    setLoading(true);
    loadConfigAnalise()
    .then(setCfg)
    .finally(() => {setLoading(false); })
  }, []);

  useEffect(() => {
    if (cfg?.dma) {
      if (cfg.dma.mode) setDmaMode(cfg.dma.mode as "mixed" | "center" | "limit");
      if (cfg.dma.insideWeight !== undefined) setDmaInside(cfg.dma.insideWeight);
      if (cfg.dma.outsideWeight !== undefined) setDmaOutside(cfg.dma.outsideWeight);
    }
  }, [cfg]);

  useEffect(() => {
    let on = true;
    setLoading(true);

    const { start, end } =
      periodo?.inicio && periodo?.fim
        ? { start: ymdLocal(periodo.inicio), end: ymdLocal(periodo.fim) }
        : periodo?.start && periodo?.end
        ? { start: ymdLocal(periodo.start), end: ymdLocal(periodo.end) }
        : rangePadraoHoje();

    getJSON(`/resumo-envasadora?start=${start}&end=${end}`)
      .then((res: any) => {
        if (!on) return;

        const env = Array.isArray(res?.envasadoras)
          ? res.envasadoras
          : Array.isArray(res)
          ? res
          : [];
        const ops = Array.isArray(res?.operadores) ? res.operadores : [];
        setOperadoresUI(ops.map((o: any) => o.nome));

        let envFiltrado = env;

        if (prodSel) {
          const alvo = String(prodSel);
          envFiltrado = envFiltrado.filter((r: any) =>
            (r?.produtos?.codigos || []).map(String).includes(alvo)
          );
        }

        if (loteSel) {
          const alvo = String(loteSel);
          envFiltrado = envFiltrado.filter((r: any) =>
            (r?.lotes?.lista || []).map(String).includes(alvo)
          );
        }

        const toArr = (s = "") =>
          String(s)
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);

        const norm: BicoResumo[] = envFiltrado.map((r: any) => {
          const total = Number(r.total ?? 0);
          const media = Number(r.media ?? 0);
          const acimaCnt = Number(r.acima ?? 0);
          const abaixoCnt = Number(r.abaixo ?? 0);
          const tolCnt = Number(r.tolerancia ?? 0);
          const ideaisCnt = Number(r.ideais ?? 0);

          const min = Number(r?.params?.min);
          const max = Number(r?.params?.max);
          const idealI = Number(r?.params?.ideal_i);
          const idealF = Number(r?.params?.ideal_f);

          const hasIdeal = Number.isFinite(idealI) && Number.isFinite(idealF);
          const to1 = (n: number) =>
            Number.isFinite(n) ? Number(n.toFixed(1)) : 0;
          const pct = (q: number) =>
            total > 0 ? Number(((q / total) * 100).toFixed(1)) : 0;

          let status: "fora" | "tolerancia" | "ideal" = "ideal";
          if (
            Number.isFinite(min) &&
            Number.isFinite(max) &&
            (media < min || media > max)
          ) {
            status = "fora";
          } else if (hasIdeal && (media < idealI || media > idealF)) {
            status = "tolerancia";
          }

          return {
            ...r,
            status,
            operador: r.colaboradores || "",
            colaboradoresArr: toArr(r.colaboradores),
            matriculasArr: toArr(r.matriculas),
            pesagens: total,
            valor: to1(media),

            produtos: r.produtos || {
              codigos: [],
              descricoes: [],
              principal: null,
              quantidade: 0,
            },
            lotes: r.lotes || { lista: [], quantidade: 0 },

            pctAbaixo: abaixoCnt,
            pctAcima: acimaCnt,
            pctTolerancia: tolCnt,
            pctIdeal: ideaisCnt,

            idealMin: hasIdeal ? to1(idealI) : 0,
            idealMax: hasIdeal ? to1(idealF) : 0,

            menor: to1(Number(r.min ?? 0)),
            maior: to1(Number(r.max ?? 0)),
          };
        });

        setData(norm);

        if (!cfg) return <div>Carregando config de análise...</div>;

        try {
          const aiRaw = envFiltrado.length
            ? analyzeResumo(
                { envasadoras: envFiltrado, operadores: ops },
                {
                  dma: {
                    mode: dmaMode,
                    insideWeight: dmaInside,
                    outsideWeight: dmaOutside,
                  },
                },
                cfg
              )
            : null;

          const ai: Insight = aiRaw
            ? {
                summary: (aiRaw.summary ?? [aiRaw.resumo]).filter(Boolean),
                risksTop: aiRaw.risksTop ?? aiRaw.riscos ?? [],
                operatorLoad: aiRaw.operatorLoad ?? aiRaw.sobrecarga ?? [],
                operatorIssues: aiRaw.operatorIssues ?? aiRaw.desvios ?? [],
                kpis: aiRaw.kpis ?? null,
                charts: aiRaw.charts ?? null,
                dma: aiRaw.dma ?? null,
                operatorUnderUtilization: aiRaw.operatorUnderUtilization ?? [],
                meta: aiRaw.meta ?? null,
              }
            : {
                summary: [],
                risksTop: [],
                operatorLoad: [],
                operatorIssues: [],
                kpis: null,
                charts: null,
              };
          setInsights(ai);
        } catch (e) {
          console.error("analyzeResumo falhou:", e);
          setInsights({
            summary: [],
            risksTop: [],
            operatorLoad: [],
            operatorIssues: [],
            kpis: null,
            charts: null,
          });
        }
      })
      .finally(() => {
        if (on) setLoading(false);
      });

    return () => {
      on = false;
    };
  }, [periodo, dmaMode, dmaInside, dmaOutside, prodSel, loteSel, cfg]);

  const filtrados = data.filter((d) =>
    filtro === "todos" ? true : d.status === filtro
  );

  const produtosAgg = useMemo(() => {
    if (!data || data.length === 0) {
      return { items: [], total: 0 };
    }

    // pega uma linha que tenha totais reais
    const linhaComTotais = data.find(
      (d) => d.totaisReaisProduto && Object.keys(d.totaisReaisProduto).length > 0
    );

    const totais = linhaComTotais?.totaisReaisProduto || {};

    type ProdutoAgg = {
      cod: string;
      desc: string;
      leituras: number;
      share: number;
    };

    const map = new Map<string, ProdutoAgg>();

    // para cada código em totaisReaisProduto (16, 31, ...)
    Object.entries(totais).forEach(([codStr, qtde]) => {
      const cod = String(codStr);

      // achar uma linha que tenha esse produto na lista
      const linha = data.find((d) =>
        d.produtos?.codigos?.includes(cod)
      );

      let desc = `Produto ${cod}`;
      if (linha && linha.produtos?.codigos && linha.produtos?.descricoes) {
        const idx = linha.produtos.codigos.indexOf(cod);
        if (idx >= 0 && linha.produtos.descricoes[idx]) {
          desc = linha.produtos.descricoes[idx];
        }
      }

      map.set(cod, {
        cod,
        desc,
        leituras: Number(qtde),
        share: 0,
      });
    });

    const items = Array.from(map.values()).sort(
      (a, b) => b.leituras - a.leituras
    );

    const total = items.reduce((s, x) => s + x.leituras, 0);

    items.forEach((x) => {
      x.share = total > 0 ? Math.round((100 * x.leituras) / total) : 0;
    });

    return { items, total };
  }, [data]);


  // Lotes agregados
  const lotesAgg = useMemo(() => {
    const map = new Map<string, LoteAgg>();
    (data || []).forEach((d) => {
      const tot = Number(d.pesagens ?? 0);
      const lotes = (d.lotes?.lista || []).filter(
        (l) => String(l || "").trim() !== ""
      );
      const n = lotes.length || 1;
      const quota = n > 0 ? tot / n : 0;

      if (n === 0) {
        const key = "—";
        const cur =
          map.get(key) || { lote: "—", leituras: 0, share: 0 };
        cur.leituras += tot;
        map.set(key, cur);
      } else {
        lotes.forEach((lote) => {
          const key = String(lote);
          const cur =
            map.get(key) || { lote: key, leituras: 0, share: 0 };
          cur.leituras += quota;
          map.set(key, cur);
        });
      }
    });
    const items = [...map.values()].sort((a, b) => b.leituras - a.leituras);
    const total = items.reduce((s, x) => s + x.leituras, 0);
    items.forEach(
      (x) => (x.share = total > 0 ? Number((100 * x.leituras) / total) : 0)
    );
    return { items, total: Math.round(total) };
  }, [data]);

  return (
    <GroupGuard allowedGroups={REQUIRED_GROUPS}>
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FiltroPeriodo onChange={setPeriodo} />
        <DmaSelector
            mode={dmaMode}
            inside={dmaInside}
            outside={dmaOutside}
            onChange={({ mode, inside, outside }) => {
              setDmaMode(mode);
              setDmaInside(inside);
              setDmaOutside(outside);
            }}
          />
          
        <div className="flex items-center gap-3">
          <FiltroTipo value={filtro} onChange={setFiltro} />
        </div>
        {/* Filtros ativos */}
        {(prodSel || loteSel) && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">Filtrando por:</span>
            {prodSel && (
              <button
                className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                onClick={()=> setProdSel(null)}
              >
                Produto #{String(prodSel)} ×
              </button>
            )}
            {loteSel && (
              <button
                className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                onClick={()=> setLoteSel(null)}
              >
                Lote {String(loteSel)} ×
              </button>
            )}
            <button
              className="ml-2 px-2 py-1 rounded bg-slate-100 hover:bg-slate-200"
              onClick={()=> { setProdSel(null); setLoteSel(null); }}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* KPIs */}
      {insights?.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard
            title="Conformidade (ideal)"
            value={insights.kpis.conformidadeGeral ?? 0}
            suffix="%"
            hint="Leituras no alvo ideal"
            tone="green"
          />
          <KpiCard
            title="Conformidade (tolerância)"
            value={insights.kpis.conformidadeTolerada ?? 0}
            suffix="%"
            hint="Dentro da faixa tolerada"
            tone="amber"
          />
          <KpiCard
            title="Violações"
            value={insights.kpis.taxaViolacoes ?? 0}
            suffix="%"
            hint="Fora da especificação"
            tone="red"
          />
          <KpiCard
            title={insights.kpis?.dmaMode === 'mixed' ? 'DMA (misto)' :
                  insights.kpis?.dmaMode === 'center' ? 'DMA (centro)' : 'DMA (limite)'}
            value={insights.kpis?.dma ?? 0}
            suffix=" g"
            hint={makeDmaHint(insights.kpis, insights.dma)}
          /> 
          {/* DMA – comparativo (pílulas compactas) */}
            {insights?.dma && (
            <div className="bg-white rounded-xl p-3 card-shadow">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">DMA – comparativo</div>
                {insights.kpis?.dmaRefValue != null && (
                  <div className="text-[11px] text-slate-500">
                    Ref.: centro ideal {insights.kpis.dmaRefValue} g
                  </div>
                )}
              </div>

              <div className="mt-2">
                <StatPills
                  items={[
                    {
                      label: 'Usado',
                      value: insights.dma.usado?.valor,
                      color: 'blue',
                      title: `Método: ${insights.dma.usado?.metodoLabel || '-'}`
                    },
                    { label: 'Centro',  value: insights.dma.centro,  color: 'green',  title: 'DMA vs centro do ideal' },
                    { label: 'Limite',  value: insights.dma.limite,  color: 'amber',  title: 'DMA vs limite ideal mais próximo' },
                    { label: 'Misto',   value: insights.dma.misto,   color: 'violet', title: 'DMA misto (dentro × fora)' },
                  ]}
                />
              </div>

              {/* Link leve para detalhes (sem ocupar espaço) */}
              <div className="mt-2 text-[11px] text-slate-500">
                <span title="O DMA 'Usado' entra no score. Os demais são comparativos.">
                  {insights.dma.usado?.metodoLabel
                    ? `Cálculo usado: ${insights.dma.usado.metodoLabel}.`
                    : 'Cálculo usado conforme configuração.'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Filtros gráficos */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* Produtos */}
            {produtosAgg.items.length > 0 && (
              <div className="bg-white rounded-xl p-4 card-shadow">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Produtos no período</h3>
                  <div className="text-xs text-slate-500">Total estimado: {produtosAgg.total} leituras</div>
                </div>
                <BarList
                  items={produtosAgg.items}
                  getKey={(p)=>`${p.cod}||${p.desc}`}
                  getLabel={(p)=>`${p.desc} ${p.cod && p.cod!=='—' ? `(${p.cod})` : ''}`}
                  getValue={(p)=>p.leituras}
                  onClick={(p)=> setProdSel(p.cod)}
                />
              </div>
            )}

            {/* Lotes */}
            {lotesAgg.items.length > 0 && (
              <div className="bg-white rounded-xl p-4 card-shadow">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Lotes no período</h3>
                  <div className="text-xs text-slate-500">Total estimado: {lotesAgg.total} leituras</div>
                </div>
                <BarList
                  items={lotesAgg.items}
                  getKey={(l)=>l.lote}
                  getLabel={(l)=>l.lote}
                  getValue={(l)=>l.leituras}
                  onClick={(l)=> setLoteSel(l.lote)}
                />
              </div>
            )}
          </div>

      
      {/* Resumo gerencial */}
      {insights && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 card-shadow lg:col-span-2">
            <h3 className="font-semibold mb-2">Resumo gerencial</h3>
            <ul className="space-y-1 text-sm leading-relaxed">
              {(insights?.summary ?? []).length === 0 && (
                <li>Nenhum insight gerado para o período.</li>
              )}
              {(data?.some(d => (d.produtos?.quantidade || 0) > 1)) && (
                <li className="text-amber-700">
                  • Mix de produtos no período em alguns bicos — evite comparar DMA/conformidade entre viscosidades diferentes.
                </li>
              )}
              {(insights?.summary ?? []).map((p, i) => (
                <li key={i}>• {p}</li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-xl p-4 card-shadow">
            <h3 className="font-semibold mb-2">Top riscos</h3>
            <ol className="text-sm space-y-1">
              {(insights?.risksTop ?? []).map(r => (
                <li key={r.bico}>
                  <span className="font-medium">Bico {String(r.bico).padStart(2,'0')}</span>
                  {' — score '}{r.score}
                  {' — média '}{Number(r.media).toFixed(1)}g
                </li>
              ))}
              {(insights?.risksTop ?? []).length === 0 && <li>Sem riscos relevantes.</li>}
            </ol>
          </div>

          <div className="bg-white rounded-xl p-4 card-shadow">
            <h3 className="font-semibold mb-2">Sobrecarga de operadores</h3>
            <ul className="text-sm space-y-1">
              {(insights?.operatorLoad ?? []).map(o => (
                <li key={o.operador}>
                  <span className="font-medium">{formatColaboradores(o.operador)}</span>
                  {`: ${o.bicos} bico(s), ${(o.share*100).toFixed(1)}% das leituras`}
                </li>
              ))}
              {(insights?.operatorLoad ?? []).length === 0 && <li>Nenhuma sobrecarga detectada.</li>}
            </ul>
          </div>

          <div className="bg-white rounded-xl p-4 card-shadow">
            <h3 className="font-semibold mb-2">Operadores com desvios</h3>
            <ul className="text-sm space-y-1">
              {(insights?.operatorIssues ?? []).map(o => (
                <li key={o.operador}>
                  <span className="font-medium">{formatColaboradores(o.operador)}</span>
                  {` — score ${o.meanScore}, críticos ${o.critPct}%, atenção ${o.atenPct}%`}
                </li>
              ))}
              {(insights?.operatorIssues ?? []).length === 0 && <li>Nenhum destaque.</li>}
            </ul>
          </div>

          {(insights?.operatorUnderUtilization?.length ?? 0) > 0 ? (
            <div className="bg-white rounded-xl p-4 card-shadow">
              <h3 className="font-semibold mb-2">
                Ociosidade potencial
                <span className="ml-2 text-xs text-slate-500">
                  (mediana {insights?.meta?.utilization?.median ?? '-'} leit./dia)
                </span>
              </h3>
              <ul className="text-sm space-y-1">
                {insights.operatorUnderUtilization!.map(o => (
                  <li key={o.operador}>
                    <span className="font-medium">{formatColaboradores(o.operador)}</span>
                    {` — ${o.leituraPorDia} leit./dia · ${o.dias} dia(s) no período`}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-4 card-shadow">
              <h3 className="font-semibold mb-1">Ociosidade potencial</h3>
              <div className="text-sm text-slate-500">
                Sem ociosidade detectada (mediana {insights?.meta?.utilization?.median ?? '-'} leit./dia).
              </div>
            </div>
          )}

        </div>
      )}

      {/* Gráficos analíticos */}
      {insights?.charts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Média por bico */}
          <ChartCard
            title="Média por bico (g)"
            description="Média de peso registrada por bico; desvio sugere calibração ou tendência de deriva."
            data={datasetFromLabelsSeries(
              insights.charts?.bicosMedia?.labels,
              [{ key: 'media', values: insights.charts?.bicosMedia?.series }]
            )}
            series={[
              { dataKey: 'media', name: 'Média (g)' } // cor automática ok
            ]}
            yAxisDomain={[90, 105]}
            yAxisticks={Array.from({ length: 16 }, (_, i) => 90 + i)}
          />

          {/* Percentuais empilhados por bico */}
          <ChartCard
            title="Distribuição por bico (%)"
            description="Distribuição das leituras por faixa: abaixo do alvo, tolerância, ideal e acima do alvo."
            data={datasetFromLabelsSeries(
              insights.charts?.bicosStackedPct?.labels,
              [
                { key: 'abaixo',      values: insights.charts?.bicosStackedPct?.series?.abaixo },
                { key: 'tolerancia',  values: insights.charts?.bicosStackedPct?.series?.tolerancia },
                { key: 'ideais',      values: insights.charts?.bicosStackedPct?.series?.ideais },
                { key: 'acima',       values: insights.charts?.bicosStackedPct?.series?.acima },
              ]
            )}
            series={[
              { dataKey: 'abaixo',     name: 'Abaixo do alvo',   color: '#ef4444', isPercent: true },
              { dataKey: 'tolerancia', name: 'Tolerância',       color: '#f59e0b', isPercent: true },
              { dataKey: 'ideais',     name: 'Ideal',            color: '#16a34a', isPercent: true },
              { dataKey: 'acima',      name: 'Acima do alvo',    color: '#2563eb', isPercent: true },
            ]}
            stacked
          />

          {/* Score por bico */}
          <ChartCard
            title="Score de risco por bico"
            description="Ranking ponderado por violação, drift e volume. Priorize inspeção/manutenção nos maiores scores."
            data={datasetFromLabelsSeries(
              insights.charts?.riskScore?.labels,
              [{ key: 'score', values: insights.charts?.riskScore?.series }]
            )}
            series={[{ dataKey: 'score', name: 'Score' }]}
             tooltipExtra={(label) => {
              const ex = insights.charts?.riskScore?.extraByLabel?.[label];
              if (!ex) return null;
              const b = ex.breakdown || {};
              return (
                <div className="space-y-0.5">
                  <div className="font-medium">Composição do score</div>
                  <div>Violação (fora esp.): {b.viola}</div>
                  <div>Drift (fora ideal): {b.drift}</div>
                  <div>Acima (%*peso): {b.sobre}</div>
                  <div>Abaixo (%*peso): {b.sub}</div>
                  <div>Tolerância (%*peso): {b.tol}</div>
                  <div>Volume (meta): {b.vol}</div>
                  <div>Gap de volume: {b.volumeGap}</div>
                  <div>Multiplicador (dias): {b.mult}</div>
                  {/* DMAs do item */}
                    {(() => {
                      const item = (insights?.risksTop || []).find(r => String(r.bico).padStart(2,'0') === label);
                      if (!item) return null;
                      return (
                        <div className="mt-2">
                          <div className="font-medium">DMAs</div>
                          <div><b>Usado ({insights?.kpis?.dmaModeLabel})</b>: {item.dma ?? '-' } g</div>
                          <div>Centro do ideal: {item.dmaCentro ?? '-' } g</div>
                          <div>Limite ideal mais próximo: {item.dmaLimite ?? '-' } g</div>
                          <div>Misto: {item.dmaMisto ?? '-' } g</div>
                        </div>
                      );
                    })()}
                  <div className="mt-1 text-[10px] text-slate-500">
                    Média: {Number(ex.media).toFixed(2)} g · Leituras: {ex.total}
                  </div>
                </div>
              );
             }}
          />

          {/* Participação por operador */}
          <ChartCard
            title="Participação por operador"
            description="Share de bicos sob responsabilidade por operador; sobrecarga aumenta risco de erro."
            data={datasetFromLabelsSeries(
              insights.charts?.operatorShare?.labels,
              [{ key: 'share', values: insights.charts?.operatorShare?.series }]
            )}
            series={[{ dataKey: 'share', name: 'Share de bicos', isPercent: true }]}
          />

        </div>
      )}

      {/* Plano de Ação (auto) */}
      {(insights?.risksTop?.length > 0) && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Planos de Ação sugeridos</h3>
            <div className="text-xs text-slate-500">
              Critérios: score ≥ {SCORE_MIN} ou violação | drift com DMA ≥ {DMA_MIN} g
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {insights.risksTop
              .filter(it =>
                Number(it.score) >= SCORE_MIN ||
                it.violacao === 1 ||
                (it.drift === 1 && Number(it.dma || 0) >= DMA_MIN)
              )
              .map((it: RiskTop) => (
                <ActionCard
                  key={it.bico}
                  item={{
                    ...it,
                    violacao: it.violacao === 1,
                    drift: it.drift === 1,
                  }}
                  operadores={operadoresUI}
                  defaultOwner={it.operadores?.[0] || operadoresUI[0] || ''}
                />
              ))
            }
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-slate-500">Carregando …</div>}

      {!loading && (!filtrados || !filtrados.length) && (
        <div className="text-sm text-slate-500">Sem informações …</div>
      )}

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {(filtrados ?? []).map(item => (
          <div key={item.bico}>
            <BicoCard
              item={{
                status: item.status,
                bico: item.bico,
                produtos: {
                principal: item.produtos?.principal
                  ? { descricao: String(item.produtos.principal) }
                  : undefined,
                quantidade: item.produtos?.quantidade,
              },
                lotes: item.lotes,
                operadora: item.operador,
                media: item.valor,
                abaixo: item.pctAbaixo,
                acima: item.pctAcima,
                pesagens: item.pesagens,
                ideais: item.pctIdeal,
                min: item.menor,
                max: item.maior,
                tolerancia: item.pctTolerancia,
              }}
            />
          </div>
        ))}
      </div>
    </section>
    </GroupGuard>
  );
}
